<script>
import { onMount } from "svelte";
import {
    playerState,
    updateTime,
    updateDuration,
    updatePlayingStatus,
} from "@app/store/playerStore.svelte.js";
import { 
    initializeAudioScrub, cleanupAudioScrub
} from "@app/store/audioScrubStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { adrState } from "@app/store/adrStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import ClosedCaptionPreview from "./ClosedCaptionPreview.svelte";
import WatermarkOverlay from "./WatermarkOverlay.svelte";
import SubtitlePreview from "./SubtitlePreview.svelte";
import TranscriptPreview from "./TranscriptPreview.svelte";
import AdPreview from "./AdPreview.svelte";
import AdrPreview from "./AdrPreview.svelte";
import StemPreview from "./StemPreview.svelte";
import { isAudioGroupType } from "@app/lib/eventGroupTypes.js";
import { createVideojsPlayerAdapter } from "@app/lib/player/videojsPlayerAdapter.js";
import { MEDIA_SOURCES } from "@app/lib/player/mediaSources.js";
import debounce from "just-debounce-it";
import tcLib from "@cclib/lib/timecode.js";
import "@videojs/html/video/player";
import "@videojs/html/ui/container";
import "@videojs/html/ui/controls";
import "@videojs/html/ui/controls-content";
import "@videojs/html/ui/controls-group";
import "@videojs/html/ui/time-slider";
import "@videojs/html/ui/slider-track";
import "@videojs/html/ui/slider-buffer";
import "@videojs/html/ui/slider-fill";
import "@videojs/html/ui/slider-thumb";
import "@videojs/html/ui/time";
import "@videojs/html/ui/fullscreen-button";
import "@videojs/html/media/hlsjs-video";
import "@videojs/html/media/mux-video";
import "@videojs/html/media/youtube-video";
import "@videojs/html/media/vimeo-video";
import * as m from '@app/paraglide/messages.js';

const POSTER = "./assets/img/cc-logo-bg.png";

/* Children (e.g. AdPreview) attach listeners to window.player while mounting,
   before this component's onMount runs, so the adapter must exist first. */
const player = createVideojsPlayerAdapter();
player.poster = POSTER;
window.player = player;

let mediaKind = $state(player.kind);

let frameTimer, sourceChangeTimer, unsubscribe;
let lastPlaying;
let lastTime = -1;

const updateTcFrame = async function () {
    try {
        updateTime(player.currentTime);
    } catch (err) {
        console.log("unable to update current time in store.", err.message);
    }
};

function handleComponentError(error) {
    console.error("Component Error:", error);
}

const goToMidi = debounce(
    (currentTime) => {
        let intValue = (currentTime * (adrState.bpm / 60)) / 0.25;
        let binaryValue = intValue.toString(2).padStart(14, "0");
        let binA = binaryValue.slice(-7);
        let binB = binaryValue.slice(0, 7);
        let spp = [0xf2, parseInt(binA, 2), parseInt(binB, 2)];
        let tc = tcLib.secToTc(
            currentTime,
            projectState.frameRate,
            projectState.dropFrame,
        );
        let tcArray = tc.split(/:|;/g);
        let midiCmd = [
            0xf0,
            0x7f,
            0x00,
            0x06,
            0x44,
            0x06,
            0x01,
            ...tcArray,
            0x00,
            0xf7,
        ];

        //console.log(`MIDI SEND: ${midiCmd} (${tc})`);
        adrState.selectedOutput.send(midiCmd);
        /* Go to Song Position */
        adrState.selectedOutput.send(spp);
    },
    500,
    true,
);

function handlePlayingChange(playing) {
    updatePlayingStatus(playing);

    if (playing) {
        //console.log("PLAY");
        if (frameTimer) {
            clearInterval(frameTimer);
        }

        frameTimer = setInterval(updateTcFrame, 50);

        if (adrState.enable && adrState.selectedOutput) {
            if (adrState.record) {
                /* Record Command */
                //console.log(`MIDI SEND: RECORD`);
                adrState.selectedOutput.send([
                    0xf0, 0x7f, 0x00, 0x06, 0x06, 0xf7,
                ]);
                adrState.selectedOutput.send([251]);
            } else {
                /* Playback command */
                //console.log(`MIDI SEND: PLAY`);
                adrState.selectedOutput.send([
                    0xf0, 0x7f, 0x00, 0x06, 0x02, 0xf7,
                ]);
                adrState.selectedOutput.send([251]);
            }
        }
    } else {
        if (frameTimer) {
            clearInterval(frameTimer);
        }

        if (adrState.enable && adrState.selectedOutput) {
            /* Pause Command */
            //console.log(`MIDI SEND: PAUSE`);
            adrState.selectedOutput.send([
                0xf0, 0x7f, 0x00, 0x06, 0x01, 0xf7,
            ]);
            adrState.selectedOutput.send([252]);
        }
    }
}

player.addEventListener("source-change", (event) => {
    console.log("Source changed to", event.detail);
    mediaKind = player.kind;
    player.playbackRate = playerState.speed;
    try {
        console.log("Cleaning up audio scrub before source change");
        cleanupAudioScrub();
    } catch (err) {
        console.log("Error cleaning up audio scrub:", err.message);
    }

    clearTimeout(sourceChangeTimer);
    sourceChangeTimer = setTimeout(() => {
        updatePlayingStatus(false);
        if (projectState?.media?.storage === MEDIA_SOURCES.LOCAL) {
            console.log("Initializing audio scrub for local storage");
            initializeAudioScrub();
        }
    }, 1500);
});

player.addEventListener("durationchange", (ev) => {
    updateDuration(ev.detail);
});

player.addEventListener("seeking", (ev) => {
    updateTime(ev.detail);
});

player.addEventListener("seeked", (ev) => {
    updateTime(ev.detail);
});

player.addEventListener("error", (ev) => {
    console.log("Media Player Error:", ev.detail);
});

function attachPlayerElement(el) {
    player.attachPlayer(el);
}

function attachMediaElement(kind) {
    return (el) => {
        player.attachMedia(el, kind);
        return () => player.releaseMedia(el);
    };
}

onMount(() => {
    unsubscribe = player.subscribe(({ playing, currentTime }) => {
        if (playing !== lastPlaying) {
            lastPlaying = playing;
            handlePlayingChange(playing);
        }

        if (currentTime !== lastTime) {
            lastTime = currentTime;
            updateTime(currentTime);
            if (adrState.enable && adrState.selectedOutput) {
                goToMidi(currentTime);
            }
        }
    });

    try {
        player.volume = 1;
        player.playbackRate = 1;
    } catch (err) {
        console.log(
            "Error setting player volume or playback rate",
            err.message,
        );
    }

    return () => {
        console.log("Destroying Media Player");
        unsubscribe?.();
        player.destroy();
        clearTimeout(sourceChangeTimer);
        if (frameTimer) {
            clearInterval(frameTimer);
        }

        cleanupAudioScrub();
    };
});
</script>

<video-player {@attach attachPlayerElement}>
    <media-container
        class="ccc-player"
        aria-label={m.player_video_player_aria_label()}
        style="aspect-ratio: {projectState?.media?.aspectRatio || "16/9"};">
        {#if mediaKind === "file"}
            <video {@attach attachMediaElement("file")} class="ccc-media ccc-native-video" playsinline preload="auto"></video>
        {:else if mediaKind === "hls"}
            <hlsjs-video class="ccc-media" {@attach attachMediaElement("hls")} playsinline></hlsjs-video>
        {:else if mediaKind === "mux"}
            <mux-video class="ccc-media" {@attach attachMediaElement("mux")} playsinline></mux-video>
        {:else if mediaKind === "dash"}
            <dash-video class="ccc-media" {@attach attachMediaElement("dash")} playsinline></dash-video>
        {:else if mediaKind === "cloudflare"}
            <cloudflare-video class="ccc-media" {@attach attachMediaElement("cloudflare")}></cloudflare-video>
        {:else if mediaKind === "youtube"}
            <youtube-video class="ccc-media" referrerpolicy="strict-origin-when-cross-origin" {@attach attachMediaElement("youtube")}></youtube-video>
        {:else if mediaKind === "vimeo"}
            <vimeo-video class="ccc-media" {@attach attachMediaElement("vimeo")}></vimeo-video>
        {:else}
            <div class="ccc-player-empty" style="background-image: url({POSTER});"></div>
        {/if}

        <svelte:boundary onerror={handleComponentError}>
            <WatermarkOverlay></WatermarkOverlay>
        </svelte:boundary>
        <svelte:boundary onerror={handleComponentError}>
            <StemPreview></StemPreview>
        </svelte:boundary>
        {#if eventGroupState[projectState.selected] && isAudioGroupType(eventGroupState[projectState.selected].type) && styleState.adrPreview}
            <svelte:boundary onerror={handleComponentError}>
                <AdrPreview></AdrPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected] && isAudioGroupType(eventGroupState[projectState.selected].type)}
            <svelte:boundary onerror={handleComponentError}>
                <AdPreview></AdPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected] && eventGroupState[projectState.selected].type === "transcription"}
            <svelte:boundary onerror={handleComponentError}>
                <TranscriptPreview></TranscriptPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected] && styleState.ccMode}
            <svelte:boundary onerror={handleComponentError}>
                <ClosedCaptionPreview></ClosedCaptionPreview>
            </svelte:boundary>
        {:else if eventGroupState[projectState.selected]}
            <svelte:boundary onerror={handleComponentError}>
                <SubtitlePreview></SubtitlePreview>
            </svelte:boundary>
        {/if}

        <media-controls class="ccc-player-chrome">
            <media-controls-content class="ccc-chrome-content">
                <media-controls-group class="ccc-chrome-bar">
                    <media-time-slider class="ccc-time-slider" step="1">
                        <media-slider-track class="ccc-slider-track">
                            <media-slider-buffer class="ccc-slider-buffer"></media-slider-buffer>
                            <media-slider-fill class="ccc-slider-fill"></media-slider-fill>
                        </media-slider-track>
                        <media-slider-thumb class="ccc-slider-thumb"></media-slider-thumb>
                    </media-time-slider>
                    <media-time class="ccc-time" type="current"></media-time>
                    <media-fullscreen-button class="ccc-fullscreen">
                        <i class="bi bi-fullscreen ccc-enter-fullscreen"></i>
                        <i class="bi bi-fullscreen-exit ccc-exit-fullscreen"></i>
                    </media-fullscreen-button>
                </media-controls-group>
            </media-controls-content>
        </media-controls>
    </media-container>
</video-player>

<style>
.ccc-player {
    --media-accent-color: #0fbc8c;
    --media-border-radius: 0;
    position: relative;
    display: block;
    width: 100%;
    overflow: hidden;
    background-color: black;
}

.ccc-media,
.ccc-player-empty {
    display: block;
    width: 100%;
    height: 100%;
}

/* Embed elements default to an inline 300x150 box. */
.ccc-media {
    min-width: 0;
    min-height: 0;
}

.ccc-native-video {
    object-fit: contain;
}

.ccc-player-empty {
    background-position: center;
    background-repeat: no-repeat;
    background-size: contain;
}

.ccc-player-chrome {
    /* Above the caption/subtitle preview (z-index 9999) */
    z-index: 10000;
    position: absolute;
    inset: auto 0 0 0;
}

.ccc-chrome-content {
    display: block;
    pointer-events: none;
    transition: opacity 0.25s;
}

.ccc-chrome-content:global(:not([data-visible])) {
    opacity: 0;
}

.ccc-chrome-bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 10px;
    pointer-events: auto;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent);
    color: white;
}

.ccc-time-slider {
    position: relative;
    display: flex;
    flex: 1;
    align-items: center;
    height: 16px;
    cursor: pointer;
}

.ccc-slider-track {
    position: absolute;
    right: 0;
    left: 0;
    height: 5px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.25);
}

.ccc-slider-buffer,
.ccc-slider-fill {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
}

.ccc-slider-buffer {
    width: var(--media-slider-buffer);
    background: rgba(255, 255, 255, 0.35);
}

.ccc-slider-fill {
    width: var(--media-slider-fill);
    background: var(--media-accent-color);
}

.ccc-time-slider:global([data-dragging]) .ccc-slider-fill {
    width: var(--media-slider-pointer);
}

.ccc-slider-thumb {
    position: absolute;
    top: 50%;
    left: var(--media-slider-fill);
    width: 13px;
    height: 13px;
    border-radius: 50%;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
    translate: -50% -50%;
}

.ccc-time-slider:global([data-dragging]) .ccc-slider-thumb {
    left: var(--media-slider-pointer);
}

.ccc-time {
    font-size: 0.8rem;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}

.ccc-fullscreen {
    display: inline-flex;
    align-items: center;
    padding: 2px 4px;
    color: white;
    cursor: pointer;
    background: none;
    border: 0;
}

.ccc-fullscreen:global([hidden]) {
    display: none;
}

.ccc-exit-fullscreen,
.ccc-fullscreen:global([data-fullscreen]) .ccc-enter-fullscreen {
    display: none;
}

.ccc-fullscreen:global([data-fullscreen]) .ccc-exit-fullscreen {
    display: inline;
}
</style>
